// src/components/.../additionalDepartureDropdown/AdditionalDepartureDropdown.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, Popover, Spin, Drawer } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { MdFlightTakeoff } from 'react-icons/md';
import './additionalDepartureDropdown.less';
import './additionalDepartureDropdown.mobile.less';
import useScreenWidth from '../../../../../hooks/useScreenSize';
import { Z_MOBILE_SHEET } from '../../../../../utils/mobileLayers';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import useSearchParamsChanges from '../../../hooks/useSearchParamsChanges';
import { useQueries } from '@tanstack/react-query';
import CustomAutoCompleteAirport from '../CustomAutoCompleteAirport';
import { fetchAirportByIata } from '@skygroup/shared/API/autoCompleteApi/autoCompleteApi';
import {
  focusSheetInput,
  primeMobileKeyboard,
} from '../../../../../utils/mobileKeyboardFocus';

interface Props {
  localDeparture?: string | null;
  setLocalDeparture?: (value: string) => void;
  departureFromModuleConfig?: any;
}

const LOCAL_STORAGE_KEY = 'customDepartures_v1';
const AIRPORT_CACHE_KEY = 'airportCache_v1';
const OTHER_VALUE = 'OTHER_DEPARTURE';

// small util: try to extract text from a React node (string/number/array/element)
function reactNodeToString(node: any): string | undefined {
  if (node === null || node === undefined) return undefined;
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    return node.map(reactNodeToString).filter(Boolean).join(' ');
  }
  if (typeof node === 'object' && node.props) {
    return reactNodeToString(node.props.children);
  }
  return undefined;
}

const AdditionalDepartureDropdown: React.FC<Props> = ({ departureFromModuleConfig, localDeparture, setLocalDeparture }) => {
  const { searchDatePicker: t } = useSelector(
    (state: RootState) => state.config.translations
  );
  const refWrapper = useRef<HTMLDivElement | null>(null);
  // Phones (width only): the departure is a chip that opens a bottom sheet —
  // a checkmark list of the saved departures, then an in-sheet airport
  // search for "other departure".
  const { isMobileWidth } = useScreenWidth();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [sheetStep, setSheetStep] = useState<'list' | 'search'>('list');
  // the search step puts the cursor in its field right away
  useEffect(() => {
    if (sheetStep !== 'search') return;
    return focusSheetInput(() => sheetSearchRef.current);
  }, [sheetStep]); // eslint-disable-line react-hooks/exhaustive-deps
  const sheetSearchRef = useRef<HTMLDivElement | null>(null);
  const { setDestinationFrom } = useSearchParamsChanges();
  const { searchParams } = useSelector((data: RootState) => data.search);
  const { config: { siteConfig } = {} } = useSelector((data: RootState) => data.config);

  const siteDefaultDepartures: any[] = ((): string[] => {
    const raw = siteConfig?.defaultDeparture;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map((v) => String(v).trim()).filter(Boolean);
    if (typeof raw === 'string') return raw.split(',').map((s) => s.trim()).filter(Boolean);
    return [];
  })();

  // load custom departures from localStorage
  const [customDepartures, setCustomDepartures] = useState<any[]>(() => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  });

  // airport cache in localStorage (map iata -> { data })
  const [localAirportCache, setLocalAirportCache] = useState<Record<string, any>>(() => {
    try {
      const raw = localStorage.getItem(AIRPORT_CACHE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });

  // merged IATA list (defaults first, then customs). If module provided a single departure make sure it's first.
  const allDepartures = useMemo(() => {
    const seen = new Set<string>();
    const merged: string[] = [];

    if (departureFromModuleConfig && typeof departureFromModuleConfig === 'string') {
      const d = departureFromModuleConfig.trim();
      if (d && !seen.has(d)) {
        seen.add(d);
        merged.push(d);
      }
    }

    siteDefaultDepartures.forEach((iata: any) => {
      if (!seen.has(iata)) {
        seen.add(iata);
        merged.push(iata);
      }
    });

    customDepartures.forEach((c: any) => {
      const iata = c?.iata;
      if (iata && !seen.has(iata)) {
        seen.add(iata);
        merged.push(iata);
      }
    });

    return merged;
  }, [departureFromModuleConfig, siteDefaultDepartures, customDepartures]);

  // ------------------- useQueries using the shared helper -------------------
  const queries = useQueries({
    queries: allDepartures.map((iata) => ({
      queryKey: ['get-airport', iata],
      queryFn: () => fetchAirportByIata(iata),
      staleTime: Infinity,
      enabled: !!iata,
    })),
  });
  // -------------------------------------------------------------------------

  // whenever any query resolves, write it to localAirportCache (localStorage)
  useEffect(() => {
    let changed = false;
    const next = { ...localAirportCache };
    queries.forEach((q, idx) => {
      const iata = allDepartures[idx];
      if (!iata) return;
      const data = q?.data ?? null;
      if (data && !next[iata]) {
        next[iata] = data;
        changed = true;
      }
    });
    if (changed) {
      setLocalAirportCache(next);
      try {
        localStorage.setItem(AIRPORT_CACHE_KEY, JSON.stringify(next));
      } catch (e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.map((q) => q?.data ? JSON.stringify(q.data) : '').join('|')]);

  // single-selected logic (we still keep selectedValue state)
  const initialSelected = localDeparture ?? searchParams.departure ?? (departureFromModuleConfig ?? siteDefaultDepartures[0] ?? customDepartures[0]?.iata ?? undefined);
  const [selectedValue, setSelectedValue] = useState<any>(initialSelected);

  // Build options using local cache first, then query results, then saved custom labels, then fallback IATA
  // NOTE: we intentionally DO NOT include "Other departure" here — that will be shown as a sticky footer.
  const options = useMemo(() => {
    const arr: { value: string; label: string }[] = [];

    const getCachedOrQuery = (iata: string, idx?: number) => {
      // 1) local storage cache
      if (localAirportCache && localAirportCache[iata]) {
        const d = localAirportCache[iata]?.data ?? localAirportCache[iata];
        if (d?.name) return d.name;
        if (d?.city_name) return d.city_name;
      }
      // 2) if useQueries result available (fallback)
      if (typeof idx === 'number' && queries[idx] && queries[idx].data) {
        const d = queries[idx].data;
        if (d?.name) return d.name;
        if (d?.city_name) return d.city_name;
      }
      return undefined;
    };

    // 1) If module passed a departure, render it first (unless it's already in defaults/customs)
    if (departureFromModuleConfig && typeof departureFromModuleConfig === 'string') {
      const iata = departureFromModuleConfig.trim();
      const inDefaults = siteDefaultDepartures.includes(iata);
      const inCustom = customDepartures.some((c) => c?.iata === iata);
      if (!inDefaults && !inCustom) {
        const idx = allDepartures.indexOf(iata);
        const apiLabel = getCachedOrQuery(iata, idx);
        const savedLabel = customDepartures.find((c) => c?.iata === iata)?.label;
        const labelCandidate = apiLabel ?? savedLabel ?? iata;
        const text = reactNodeToString(labelCandidate);
        arr.push({ value: iata, label: text ?? String(iata) });
      }
    }

    // 2) defaults first (siteDefaultDepartures already parsed to array)
    siteDefaultDepartures.forEach((iata: string) => {
      const customMatch = customDepartures.find((c) => c?.iata === iata);
      const savedLabel = customMatch?.label;
      const idx = allDepartures.indexOf(iata);
      const apiLabel = getCachedOrQuery(iata, idx);
      const labelCandidate = apiLabel ?? savedLabel ?? iata;
      const text = reactNodeToString(labelCandidate);
      arr.push({ value: iata, label: text ?? String(iata) });
    });

    // 3) custom departures next (that are not duplicates)
    customDepartures.forEach((c: any) => {
      const iata = c?.iata;
      if (!iata) return;
      if (siteDefaultDepartures.includes(iata)) return;
      const savedLabel = c?.label;
      const idx = allDepartures.indexOf(iata);
      const apiLabel = getCachedOrQuery(iata, idx);
      const labelCandidate = apiLabel ?? savedLabel ?? iata;
      const text = reactNodeToString(labelCandidate);
      arr.push({ value: iata, label: text ?? String(iata) });
    });

    return arr;
  }, [departureFromModuleConfig, siteDefaultDepartures, customDepartures, localAirportCache, queries, allDepartures]);

  // fetch indicator = any query loading
  const anyFetching = queries.some((q) => q?.isFetching);

  // persist customDepartures when changed
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(customDepartures));
    } catch (e) {
      // ignore
    }
  }, [customDepartures]);

  // Popover open state and small openRequested guard to avoid race with Select internals
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [openRequested, setOpenRequested] = useState(false);

  // NEW: suppress immediate select right after popover opens (to avoid mount-race select)
  const [suppressImmediateSelect, setSuppressImmediateSelect] = useState(false);

  useEffect(() => {
    if (!openRequested) return;
    // set suppress flag and open popover on next tick
    setSuppressImmediateSelect(true);
    const id = window.setTimeout(() => {
      setPopoverVisible(true);
      setOpenRequested(false);
      // clear suppression after a short delay (next tick is OK)
      const clearId = window.setTimeout(() => {
        setSuppressImmediateSelect(false);
        window.clearTimeout(clearId);
      }, 50);
      window.clearTimeout(id);
    }, 0);
    return () => {
      // cleanup timers if unmounted/changed
      setSuppressImmediateSelect(false);
    };
  }, [openRequested]);

  const [selectedFromPopover, setSelectedFromPopover] = useState<string | null>(null);
  // Control Select dropdown open state
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleChange = (value: string) => {
    // selecting a normal option — close popover (if open) and update selection
    setPopoverVisible(false);
    setDropdownOpen(false);
    setSelectedValue(value);
    if (typeof setLocalDeparture === 'function') {
      setLocalDeparture(value);
    } else {
      setDestinationFrom(value);
    }
  };

  // when user picks from popover, save to customDepartures and/or persist label
  const handlePopoverSelect = (iataCode: string, friendlyLabel?: any) => {
    if (!iataCode) return;
    if (suppressImmediateSelect && iataCode === selectedValue) {
      setSuppressImmediateSelect(false);
      return;
    }
    const exists = customDepartures.some((c) => c?.iata === iataCode);
    const savedLabel = typeof friendlyLabel === 'string' ? friendlyLabel : undefined;
    if (!exists) {
      const next = [
        ...customDepartures,
        {
          iata: iataCode,
          label: savedLabel,
          savedAt: Date.now(),
        },
      ];
      setCustomDepartures(next);
    } else if (savedLabel) {
      setCustomDepartures((prev) =>
        prev.map((c) => (c.iata === iataCode ? { ...c, label: savedLabel } : c))
      );
    }

    // optionally store API result for that iata in localAirportCache if present in queries
    const idx = allDepartures.indexOf(iataCode);
    const qData = queries[idx]?.data ?? null;
    if (qData) {
      setLocalAirportCache((prev) => {
        const next = { ...prev, [iataCode]: qData };
        try {
          localStorage.setItem(AIRPORT_CACHE_KEY, JSON.stringify(next));
        } catch (e) {}
        return next;
      });
    }

    setSelectedValue(iataCode);
    setPopoverVisible(false);
    if (typeof setLocalDeparture === 'function') {
      setLocalDeparture(iataCode);
    } else {
      setDestinationFrom(iataCode);
    }
    setSelectedFromPopover(iataCode);
  };

  // when user clicks "Other departure" footer
  const openOther = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDropdownOpen(false);
    // request to open on next tick to avoid Select race
    setOpenRequested(true);
  };

  // render a sticky footer below the scrollable options using dropdownRender
  const dropdownRender = (menu: React.ReactNode) => {
    return (
      <div>
        <div className="departure-options">{menu}</div>
        <div
          className="additional-departure-footer"
          role="button"
          tabIndex={0}
          onClick={openOther}
        >
          <button className="other-departure">{t.otherDeparture}</button>
        </div>
      </div>
    );
  };

  if (isMobileWidth) {
    const selectedOption = options.find((o) => o.value === selectedValue);
    const closeSheet = () => {
      setIsSheetOpen(false);
      setSheetStep('list');
    };
    // The sheet is portalled, but React still bubbles its events through
    // this component's parents (the destination field), whose handlers
    // would otherwise react to taps inside the sheet.
    const isolate = {
      onClick: (e: React.SyntheticEvent) => e.stopPropagation(),
      onMouseDown: (e: React.SyntheticEvent) => e.stopPropagation(),
      onTouchStart: (e: React.SyntheticEvent) => e.stopPropagation(),
      onKeyDown: (e: React.SyntheticEvent) => e.stopPropagation(),
      onFocus: (e: React.SyntheticEvent) => e.stopPropagation(),
    };
    return (
      <div className="additional-departure-dropdown-wrapper" ref={refWrapper}>
        <button
          type="button"
          className="dep-chip"
          onClick={(e) => {
            e.stopPropagation();
            setIsSheetOpen(true);
          }}
          onMouseDown={isolate.onMouseDown}
          onTouchStart={isolate.onTouchStart}
          aria-haspopup="dialog"
          // compact: the code only — the full airport name is in the sheet
          title={selectedOption?.label}
          aria-label={`${t.departure}: ${selectedOption?.label ?? selectedValue ?? ''}`}
        >
          <MdFlightTakeoff className="dep-chip__icon" />
          <span className="dep-chip__label">{selectedValue ?? t.departure}</span>
          {anyFetching ? <Spin size="small" /> : <DownOutlined className="dep-chip__caret" />}
        </button>

        <Drawer
          open={isSheetOpen}
          onClose={closeSheet}
          placement="bottom"
          height="auto"
          closable={false}
          push={false}
          zIndex={Z_MOBILE_SHEET}
          className="dep-sheet"
          destroyOnClose
        >
          <div className="dep-sheet__inner" {...isolate}>
          <span className="dep-sheet__grabber" aria-hidden="true" />
          <div className="dep-sheet__head">
            {sheetStep === 'search' ? (
              <button
                type="button"
                className="dep-sheet__icon-btn"
                onClick={() => setSheetStep('list')}
                aria-label={t.departure}
              >
                <LeftOutlined className="dep-sheet__back" />
              </button>
            ) : (
              <span className="dep-sheet__icon-btn is-placeholder" />
            )}
            <span className="dep-sheet__title">
              {sheetStep === 'search' ? t.otherDeparture : t.departure}
            </span>
            <button
              type="button"
              className="dep-sheet__icon-btn"
              onClick={closeSheet}
              aria-label={t.departure}
            >
              <CloseOutlined />
            </button>
          </div>

          {sheetStep === 'list' ? (
            <div className="dep-sheet__body">
              <div className="dep-sheet__list" role="listbox">
                {options.map((o) => {
                  const isSelected = o.value === selectedValue;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`dep-row${isSelected ? ' is-selected' : ''}`}
                      onClick={() => {
                        handleChange(o.value);
                        closeSheet();
                      }}
                    >
                      <span className="dep-row__icon">
                        <MdFlightTakeoff />
                      </span>
                      <span className="dep-row__label">{o.label}</span>
                      <span className="dep-row__code">{o.value}</span>
                      <span className="dep-row__check">
                        {isSelected && <CheckOutlined />}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="dep-row dep-row--other"
                onClick={() => {
                  // inside the tap, so iOS raises the keyboard for the search
                  primeMobileKeyboard();
                  setSheetStep('search');
                }}
              >
                <span className="dep-row__icon">
                  <PlusOutlined />
                </span>
                <span className="dep-row__label">{t.otherDeparture}</span>
                <RightOutlined className="dep-row__chevron" />
              </button>
            </div>
          ) : (
            <div className="dep-sheet__body dep-sheet__search" ref={sheetSearchRef}>
              <div className="dep-sheet__search-field">
                <SearchOutlined className="dep-sheet__search-icon" />
                <CustomAutoCompleteAirport
                  options={[]}
                  iataCode={selectedValue}
                  placeholder={t.searchDepartureAirport}
                  startInSearchMode
                  // a short in-sheet list: no virtual scroller (its filler
                  // is sized for a floating popup)
                  virtual={false}
                  hideArrow
                  selectedAirport={selectedFromPopover ?? undefined}
                  getPopupContainer={() => sheetSearchRef.current || document.body}
                  // The field also calls setSelectedAirport on mount with the
                  // current airport (the race the desktop popover guards with
                  // suppressImmediateSelect) — only a real pick (onSelect)
                  // may change the departure here.
                  setSelectedAirport={() => undefined}
                  onSelect={(value: any, option: any) => {
                    const pickedIata =
                      typeof value === 'string' ? value : value?.value ?? value?.iata_code;
                    const maybeLabel = option?.name ?? option?.airport_name;
                    const friendly = typeof maybeLabel === 'string' ? maybeLabel : undefined;
                    if (pickedIata) {
                      handlePopoverSelect(pickedIata, friendly);
                      closeSheet();
                    }
                  }}
                  width="100%"
                />
              </div>
            </div>
          )}
          </div>
        </Drawer>
      </div>
    );
  }

  return (
    <div className="additional-departure-dropdown-wrapper" ref={refWrapper}>
      <Popover
        content={
          <div style={{ minWidth: 'min(320px, calc(100vw - 48px))' }}>
            <CustomAutoCompleteAirport
              options={[]}
              iataCode={selectedValue}
              placeholder="Search destination"
              selectedAirport={selectedFromPopover ?? undefined}
              setSelectedAirport={(v: any) => {
                const pickedIata = Array.isArray(v) ? v[0] : typeof v === 'string' ? v : v?.value ?? v?.iata_code ?? undefined;
                if (pickedIata) {
                  const maybeLabel = typeof v === 'object' ? (v?.label ?? v?.name) : undefined;
                  const friendly = typeof maybeLabel === 'string' ? maybeLabel : undefined;
                  handlePopoverSelect(pickedIata, friendly);
                }
              }}
              onSelect={(value: any) => {
                const pickedIata = typeof value === 'string' ? value : value?.value ?? value?.iata_code;
                const maybeLabel = typeof value === 'object' ? (value?.label ?? value?.name) : undefined;
                const friendly = typeof maybeLabel === 'string' ? maybeLabel : undefined;
                if (pickedIata) handlePopoverSelect(pickedIata, friendly);
              }}
              width="min(320px, calc(100vw - 48px))"
            />
          </div>
        }
        trigger="click"
        open={popoverVisible}
        onOpenChange={(v) => {
          if (!v) setPopoverVisible(false);
        }}
        getPopupContainer={() => refWrapper.current || document.body}
        placement="bottomLeft"
      >
        <Select
          className="additional-departure-dropdown"
          popupClassName="additional-departure-dropdown-options"
          dropdownRender={dropdownRender}
          value={selectedValue}
          onChange={handleChange}
          options={options.map((o) => ({ ...o }))}
          suffixIcon={anyFetching ? <Spin size="small" /> : undefined}
          open={dropdownOpen}
          onDropdownVisibleChange={setDropdownOpen}
        />
      </Popover>
    </div>
  );
};

export default AdditionalDepartureDropdown;
